import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, MenuItem } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { useContextStateManagement } from "../context/ContextApi";
import { TimeZoneDropdown } from "../onBoardingTemplateForms/TimeZoneDropdown";

// Phase 1: single hardcoded workflow preset. Once more workflow types exist this
// becomes a dropdown of presets instead of a constant.
const WORKFLOW_KEY = "outbound_confirmation_phase1";
const WORKFLOW_LABEL = "SPM Outbound Confirmation";
// Required by the backend upsert (CallerPhoneNumberSourceKey) but not yet an
// admin-facing concept for this preset — hardcoded until the patient-list schema
// for this workflow is exposed in the UI.
const CALLER_PHONE_SOURCE_KEY = "phoneNumber";
const ALL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_SLOTS = [
    "08AM", "09AM", "10AM", "11AM", "12PM", "01PM", "02PM", "03PM",
    "04PM", "05PM", "06PM", "07PM", "08PM", "09PM", "10PM",
];

// Mirrors the server-side rules in server/src/utils/leadSchemaValidation.js — this is a UX
// nicety only, the backend is the authoritative validator on every write.
const ATTRIBUTE_NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const RESERVED_ATTRIBUTE_NAMES = ["__proto__", "constructor", "prototype"];
const MAX_ATTRIBUTES = 20;
const MAX_ATTRIBUTE_VALUE_LENGTH = 500;
const MAX_ATTRIBUTE_NAME_LENGTH = 64;

const validateAttributeName = (name) => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return "Name is required";
    if (trimmed.length > MAX_ATTRIBUTE_NAME_LENGTH) return `Max ${MAX_ATTRIBUTE_NAME_LENGTH} characters allowed`;
    if (RESERVED_ATTRIBUTE_NAMES.includes(trimmed.toLowerCase())) return "This name is reserved";
    if (!ATTRIBUTE_NAME_REGEX.test(trimmed)) return "Only letters, numbers, and underscores; must not start with a number";
    return "";
};

const validateAttributeValue = (value) => {
    if (String(value || "").length > MAX_ATTRIBUTE_VALUE_LENGTH) return `Max ${MAX_ATTRIBUTE_VALUE_LENGTH} characters allowed`;
    return "";
};

export const ScheduledWorkflows = () => {
    const { PracticeId, token, fetchUserRecord, usersPostRequest, updateRequest } = useContextStateManagement();

    const [scheduleId, setScheduleId] = useState(null);
    const [webhookUrl, setWebhookUrl] = useState("");
    const [scheduleTime, setScheduleTime] = useState("08AM");
    const [scheduleTimezone, setScheduleTimezone] = useState("America/New_York");
    const [scheduleDays, setScheduleDays] = useState(ALL_DAYS);
    const [providerId, setProviderId] = useState("");
    const [providerOptions, setProviderOptions] = useState([]);
    const [isEnabled, setIsEnabled] = useState(true);
    const [lastRun, setLastRun] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [oneTimeSecret, setOneTimeSecret] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [saveSuccess, setSaveSuccess] = useState("");
    const [attributeRows, setAttributeRows] = useState([{ name: "", value: "" }]);
    const [runNowResult, setRunNowResult] = useState(null);

    useEffect(() => {
        const loadProviders = async () => {
            if (!PracticeId || !token) return;

            // GET /api/LeadGenerationOnboarding/:PracticeId responds with
            // { providersData: [...], functions: [...] }, not a bare array - unwrap before checking.
            const providerData = await fetchUserRecord(token, `/api/LeadGenerationOnboarding/${PracticeId}`);
            setProviderOptions(Array.isArray(providerData?.providersData) ? providerData.providersData : []);
        };

        loadProviders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [PracticeId, token]);

    useEffect(() => {
        const loadSchedule = async () => {
            if (!PracticeId || !token) return;

            // GET /api/PracticeWebhookSchedule/:PracticeId responds with { schedules: [...] },
            // not a bare array — unwrap the envelope before checking. Checking Array.isArray on
            // the raw response was always false, so an existing (possibly admin-disabled)
            // schedule never loaded and the next Save silently re-created it with defaults
            // (IsEnabled: true, all 7 days).
            const userData = await fetchUserRecord(token, `/api/PracticeWebhookSchedule/${PracticeId}`);
            const schedules = userData && Array.isArray(userData.schedules) ? userData.schedules : null;
            if (!schedules) {
                // "no record" / token-expired string / undefined — nothing saved yet, leave defaults.
                return;
            }

            const existing = schedules.find((row) => row.WorkflowKey === WORKFLOW_KEY);
            if (!existing) return;

            setScheduleId(existing.Id);
            setWebhookUrl(existing.WebhookUrl || "");
            setScheduleTime(existing.ScheduleTime || "08AM");
            setScheduleTimezone(existing.ScheduleTimezone || "America/New_York");
            setScheduleDays(
                Array.isArray(existing.ScheduleDays) && existing.ScheduleDays.length > 0
                    ? existing.ScheduleDays
                    : ALL_DAYS
            );
            setProviderId(existing.ProviderId != null ? String(existing.ProviderId) : "");
            setIsEnabled(existing.IsEnabled !== false);
            setLastRun({
                lastRunAt: existing.LastRunAt,
                lastRunStatus: existing.LastRunStatus,
                lastRunPatientCount: existing.LastRunPatientCount,
            });

            const existingAttributes = existing.Attributes && typeof existing.Attributes === "object"
                ? Object.entries(existing.Attributes).map(([name, value]) => ({ name, value: String(value) }))
                : [];
            setAttributeRows(existingAttributes.length > 0 ? existingAttributes : [{ name: "", value: "" }]);
        };

        loadSchedule();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [PracticeId, token]);

    const handleTimezoneChange = (e) => {
        setScheduleTimezone(e.target.value);
    };

    const handleAttributeRowChange = (index, field, value) => {
        setAttributeRows((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const handleAddAttributeRow = () => {
        setAttributeRows((rows) => (rows.length >= MAX_ATTRIBUTES ? rows : [...rows, { name: "", value: "" }]));
    };

    const handleRemoveAttributeRow = (index) => {
        setAttributeRows((rows) => (rows.length <= 1 ? [{ name: "", value: "" }] : rows.filter((_, i) => i !== index)));
    };

    // Returns null (and sets loadError) if any row fails client-side validation, otherwise
    // the flattened {name: value} object ready to send as Attributes.
    const buildAttributesPayload = () => {
        const nonBlankRows = attributeRows.filter((row) => row.name.trim() !== "" || row.value.trim() !== "");

        if (nonBlankRows.length > MAX_ATTRIBUTES) {
            setLoadError(`Max ${MAX_ATTRIBUTES} attributes allowed`);
            return null;
        }

        const seenNames = new Set();
        for (const row of nonBlankRows) {
            const nameError = validateAttributeName(row.name);
            if (nameError) {
                setLoadError(`Attribute "${row.name || "(blank)"}": ${nameError}`);
                return null;
            }
            const trimmedName = row.name.trim();
            if (seenNames.has(trimmedName.toLowerCase())) {
                setLoadError(`Duplicate attribute name: ${trimmedName}`);
                return null;
            }
            seenNames.add(trimmedName.toLowerCase());

            const valueError = validateAttributeValue(row.value);
            if (valueError) {
                setLoadError(`Attribute "${trimmedName}": ${valueError}`);
                return null;
            }
        }

        const attributes = {};
        nonBlankRows.forEach((row) => {
            attributes[row.name.trim()] = String(row.value);
        });
        return attributes;
    };

    const handleSave = async (e) => {
        e.target.disabled = true;
        setLoadError("");
        setSaveSuccess("");

        if (!webhookUrl.trim()) {
            setLoadError("Webhook URL is required");
            e.target.disabled = false;
            return;
        }
        if (!providerId) {
            setLoadError("Provider is required");
            e.target.disabled = false;
            return;
        }

        const attributes = buildAttributesPayload();
        if (attributes === null) {
            e.target.disabled = false;
            return;
        }

        try {
            if (scheduleId) {
                const obj = {
                    Label: WORKFLOW_LABEL,
                    WebhookUrl: webhookUrl,
                    ScheduleTime: scheduleTime,
                    ScheduleTimezone: scheduleTimezone,
                    ScheduleDays: scheduleDays,
                    ProviderId: providerId,
                    IsEnabled: isEnabled,
                    Attributes: attributes,
                };
                const userData = await updateRequest(token, `/api/PracticeWebhookSchedule/${scheduleId}`, obj);
                if (userData && userData.schedule) {
                    setLastRun({
                        lastRunAt: userData.schedule.LastRunAt,
                        lastRunStatus: userData.schedule.LastRunStatus,
                        lastRunPatientCount: userData.schedule.LastRunPatientCount,
                    });
                    setSaveSuccess(`Saved - ${Object.keys(userData.schedule.Attributes || {}).length} attribute(s) stored.`);
                } else if (userData) {
                    setLoadError(typeof userData === "string" ? userData : "Failed to update schedule");
                }
            } else {
                const obj = {
                    PracticeId,
                    WorkflowKey: WORKFLOW_KEY,
                    Label: WORKFLOW_LABEL,
                    WebhookUrl: webhookUrl,
                    ScheduleTime: scheduleTime,
                    ScheduleTimezone: scheduleTimezone,
                    ScheduleDays: scheduleDays,
                    CallerPhoneNumberSourceKey: CALLER_PHONE_SOURCE_KEY,
                    ProviderId: providerId,
                    IsEnabled: isEnabled,
                    Attributes: attributes,
                };
                const userData = await usersPostRequest(token, "/api/PracticeWebhookSchedule", obj);
                if (userData && userData.schedule) {
                    setScheduleId(userData.schedule.Id);
                    setLastRun({
                        lastRunAt: userData.schedule.LastRunAt,
                        lastRunStatus: userData.schedule.LastRunStatus,
                        lastRunPatientCount: userData.schedule.LastRunPatientCount,
                    });
                    setSaveSuccess(`Saved - ${Object.keys(userData.schedule.Attributes || {}).length} attribute(s) stored.`);
                    if (userData.webhookAuthSecret) {
                        setOneTimeSecret(userData.webhookAuthSecret);
                    }
                } else if (userData) {
                    setLoadError(typeof userData === "string" ? userData : "Failed to create schedule");
                }
            }
        } finally {
            e.target.disabled = false;
        }
    };

    const handleRunNow = async (e) => {
        if (!scheduleId) return;
        e.target.disabled = true;
        setRunNowResult(null);
        try {
            const userData = await usersPostRequest(token, `/api/PracticeWebhookSchedule/${scheduleId}/run-now`, {});
            if (userData && typeof userData === "object" && "status" in userData) {
                setRunNowResult({
                    ok: userData.status === "success",
                    status: userData.httpStatus,
                    body: userData.responseBody,
                });
            } else {
                setRunNowResult({ ok: false, status: null, body: typeof userData === "string" ? userData : "Run now failed" });
            }
        } finally {
            e.target.disabled = false;
        }
    };

    return (
        <>
            <p className="heading">Scheduled Workflows</p>
            <table className="table generalTable">
                <thead>
                    <tr className="call-row-heading">
                        <th className="callSettingsHeading">Workflow</th>
                        <th className="callSettingsHeading">Provider <span className="required-fields">*</span></th>
                        <th className="callSettingsHeading">Webhook URL <span className="required-fields">*</span></th>
                        <th className="callSettingsHeading">Schedule time</th>
                        <th className="callSettingsHeading">Timezone</th>
                        <th className="callSettingsHeading">Days</th>
                        <th className="callSettingsHeading">Enabled</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{WORKFLOW_LABEL}</td>
                        <td>
                            <select
                                className="form-select"
                                id="scheduledWorkflowProviderId"
                                value={providerId}
                                onChange={(e) => setProviderId(e.target.value)}
                            >
                                <option value="">Select provider ...</option>
                                {providerOptions.map((provider) => (
                                    <option key={provider.Id} value={provider.Id}>
                                        {provider.ProviderName}
                                    </option>
                                ))}
                            </select>
                        </td>
                        <td>
                            <input
                                type="url"
                                id="scheduledWorkflowWebhookUrl"
                                className="form-control"
                                style={{ height: 32, minWidth: 220 }}
                                placeholder="https://n8n.allyzent.com/webhook/..."
                                value={webhookUrl}
                                onChange={(e) => setWebhookUrl(e.target.value)}
                            />
                        </td>
                        <td>
                            <select
                                className="form-select dropdownCallSettingsTimeOfCall"
                                id="scheduledWorkflowTime"
                                value={scheduleTime}
                                onChange={(e) => setScheduleTime(e.target.value)}
                            >
                                {TIME_SLOTS.map((slot) => (
                                    <option key={slot} value={slot}>
                                        {slot.replace(/(AM|PM)/, " $1")}
                                    </option>
                                ))}
                            </select>
                        </td>
                        <td style={{ width: "200px" }}>
                            <TimeZoneDropdown isBgBlack={false} selectedTimezone={scheduleTimezone} onChange={handleTimezoneChange} />
                        </td>
                        <td>
                            <Autocomplete
                                sx={{
                                    backgroundColor: "#fff", color: "#000", width: 275, borderRadius: ".25rem",
                                    "& .MuiOutlinedInput-root": { height: 32, flexWrap: "nowrap" },
                                    "& .MuiAutocomplete-clearIndicator": {
                                        color: "#000 !important",
                                        backgroundColor: "#f0f0f0 !important",
                                        borderRadius: "50%",
                                        "&:hover": { backgroundColor: "#d6d6d6" },
                                    },
                                    "& .MuiAutocomplete-popupIndicator": { color: "#000" },
                                    "& .MuiSvgIcon-root": { color: "#000" },
                                }}
                                multiple
                                id="scheduledWorkflowDays"
                                value={scheduleDays}
                                onChange={(event, newValue) => setScheduleDays(newValue)}
                                popupIcon={<KeyboardArrowDownOutlinedIcon />}
                                options={ALL_DAYS}
                                getOptionLabel={(option) => option}
                                disableCloseOnSelect
                                // Individual removable chips wrap onto multiple lines and blow up the
                                // table row once more than ~2 days are selected. A single summary label
                                // (day count, or the day names when 2 or fewer are picked) stays on one
                                // line regardless of selection size — deselecting still works via the
                                // checkmarked dropdown list in renderOption below.
                                renderTags={(value) => (
                                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {value.length === 0
                                            ? ""
                                            : value.length === ALL_DAYS.length
                                                ? "Every day"
                                                : value.length <= 2
                                                    ? value.join(", ")
                                                    : `${value.length} days selected`}
                                    </span>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className="textField"
                                        placeholder="Select Days ..."
                                        size="small"
                                        color="secondary"
                                    />
                                )}
                                renderOption={(props, option, { selected }) => (
                                    <MenuItem
                                        {...props}
                                        key={option}
                                        value={option}
                                        sx={{ fontSize: "0.75rem", justifyContent: "space-between" }}
                                    >
                                        {option}
                                        {selected ? <CheckIcon color="info" /> : null}
                                    </MenuItem>
                                )}
                            />
                        </td>
                        <td>
                            <input
                                type="checkbox"
                                id="scheduledWorkflowEnabled"
                                checked={isEnabled}
                                onChange={(e) => setIsEnabled(e.target.checked)}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <p className="heading mt-3">Attributes sent to webhook</p>
            <p className="mb-2">
                These name/value pairs are sent verbatim as the JSON body when this workflow fires — e.g. <code>office</code>, <code>days_from_today</code>.
            </p>
            {attributeRows.map((row, index) => {
                const nameError = row.name.trim() !== "" ? validateAttributeName(row.name) : "";
                const valueError = validateAttributeValue(row.value);
                return (
                    <div key={index} className="d-flex align-items-start gap-2 mb-2">
                        <div>
                            <input
                                type="text"
                                className={`form-control ${nameError ? "is-invalid" : ""}`}
                                style={{ height: 32, minWidth: 180 }}
                                placeholder="Name (e.g. office)"
                                value={row.name}
                                onChange={(e) => handleAttributeRowChange(index, "name", e.target.value)}
                            />
                            {nameError && <div className="invalid-feedback d-block">{nameError}</div>}
                        </div>
                        <div>
                            <input
                                type="text"
                                className={`form-control ${valueError ? "is-invalid" : ""}`}
                                style={{ height: 32, minWidth: 220 }}
                                placeholder="Value"
                                value={row.value}
                                onChange={(e) => handleAttributeRowChange(index, "value", e.target.value)}
                            />
                            {valueError && <div className="invalid-feedback d-block">{valueError}</div>}
                        </div>
                        <span
                            title="Remove attribute"
                            className="text-danger cursor-pointer fs-6 mt-2"
                            onClick={() => handleRemoveAttributeRow(index)}
                        >
                            &minus;
                        </span>
                    </div>
                );
            })}
            <button
                type="button"
                className="simple-btn px-3 mb-3"
                disabled={attributeRows.length >= MAX_ATTRIBUTES}
                onClick={handleAddAttributeRow}
            >
                + Add Attribute
            </button>

            <div>
                <button
                    type="submit"
                    className="mt-3 mb-1 simple-btn px-4"
                    disabled={isSaving}
                    onClick={async (e) => {
                        setIsSaving(true);
                        await handleSave(e);
                        setIsSaving(false);
                    }}
                >
                    Save
                </button>

                {scheduleId && (
                    <button
                        type="button"
                        className="mt-3 mb-1 ms-2 simple-btn px-4"
                        onClick={handleRunNow}
                    >
                        Run Now
                    </button>
                )}
            </div>

            {runNowResult && (
                <div className={`callTimeMsg position-relative ${runNowResult.ok ? "text-success" : "text-danger"}`}>
                    Run now {runNowResult.ok ? "succeeded" : "failed"} - HTTP {runNowResult.status ?? "?"}: {typeof runNowResult.body === "string" ? runNowResult.body : JSON.stringify(runNowResult.body)}
                </div>
            )}

            {loadError && (
                <div className="callTimeMsg position-relative">{loadError}</div>
            )}

            {saveSuccess && (
                <div className="callTimeMsg position-relative text-success">{saveSuccess}</div>
            )}

            {lastRun && lastRun.lastRunAt && (
                <p className="mt-2">
                    Last run: {new Date(lastRun.lastRunAt).toLocaleString("en-US", { timeZone: scheduleTimezone })} - {lastRun.lastRunStatus || "unknown"}, {lastRun.lastRunPatientCount ?? 0} patients
                </p>
            )}

            {oneTimeSecret && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
                    <div className="bg-white p-4 rounded" style={{ maxWidth: 520 }}>
                        <p className="fw-bold">Copy this into your n8n webhook's header auth credential - it will not be shown again:</p>
                        <code className="d-block p-2 mb-3" style={{ wordBreak: "break-all", background: "#f0f0f0" }}>{oneTimeSecret}</code>
                        <button
                            type="button"
                            className="simple-btn px-4"
                            onClick={() => setOneTimeSecret(null)}
                        >
                            I've copied it
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};
